import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  FormControl,
  TextField,
  Button,
  Collapse,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import DateRangeFilterField from './DateRangeFilterField';

function findFilterOption(options, value) {
  if (value === '' || value == null) return null;
  return (
    (options || []).find((option) => String(option?.id) === String(value)) ?? null
  );
}

function SearchableFilterAutocomplete({
  label,
  options,
  value,
  onChange,
  onOpen,
  loading = false,
  noOptionsText = 'No options found',
}) {
  return (
    <Autocomplete
      fullWidth
      options={Array.isArray(options) ? options : []}
      value={findFilterOption(options, value)}
      onChange={(_, selected) => onChange(selected?.id ?? '')}
      onOpen={() => {
        if (typeof onOpen === 'function') onOpen();
      }}
      loading={loading}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, selected) =>
        String(option?.id) === String(selected?.id)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={loading ? 'Loading…' : noOptionsText}
    />
  );
}

const LeadFilters = ({
  showFilters,
  filters,
  sort,
  noActivity,
  statusOptions,
  usersList,
  statusFilterLoading = false,
  usersFilterLoading = false,
  onStatusOpen,
  onUsersOpen,
  onFilterChange,
  onDateRangeChange,
  onSortChange,
  onNoActivityChange,
  onClearFilters,
}) => {
  const statusList = Array.isArray(statusOptions) ? statusOptions : [];
  const userOptions = Array.isArray(usersList) ? usersList : [];

  const handleUsersOpen = () => {
    if (typeof onUsersOpen === 'function') onUsersOpen();
  };

  const handleDateRangeChange = ({ date_from, date_to }) => {
    if (typeof onDateRangeChange === 'function') {
      onDateRangeChange({ date_from, date_to });
      return;
    }
    onFilterChange('date_from', date_from || '');
    onFilterChange('date_to', date_to || '');
  };

  return (
    <Collapse in={showFilters} unmountOnExit={false}>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Filters & Sorting"
          action={
            <Button onClick={onClearFilters} color="primary">
              Clear All
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <SearchableFilterAutocomplete
                  label="Status"
                  options={statusList}
                  value={filters.status_id}
                  onChange={(next) => onFilterChange('status_id', next)}
                  onOpen={onStatusOpen}
                  loading={statusFilterLoading}
                  noOptionsText="No status found"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <Autocomplete
                  value={findFilterOption(userOptions, filters.assignee_id)}
                  onChange={(_, value) => onFilterChange('assignee_id', value ? value.id : '')}
                  onOpen={handleUsersOpen}
                  loading={usersFilterLoading}
                  options={userOptions}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assignee"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersFilterLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  noOptionsText={usersFilterLoading ? 'Loading…' : 'No assignee found'}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <Autocomplete
                  value={findFilterOption(userOptions, filters.author_id)}
                  onChange={(_, value) => onFilterChange('author_id', value ? value.id : '')}
                  onOpen={handleUsersOpen}
                  loading={usersFilterLoading}
                  options={userOptions}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Created By"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersFilterLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  noOptionsText={usersFilterLoading ? 'Loading…' : 'No author found'}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="No Activity Since (Days)"
                type="number"
                value={noActivity.no_activity_since_days}
                onChange={(e) => onNoActivityChange(e.target.value)}
                InputProps={{
                  inputProps: { min: 1 },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DateRangeFilterField
                dateFrom={filters.date_from}
                dateTo={filters.date_to}
                onChange={handleDateRangeChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sort.sort_by}
                  onChange={(e) => onSortChange({ ...sort, sort_by: e.target.value })}
                  label="Sort By"
                >
                  <MenuItem value="updated_at">Last Updated</MenuItem>
                  <MenuItem value="created_at">Created AT</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sort Direction</InputLabel>
                <Select
                  value={sort.sort_direction}
                  onChange={(e) => onSortChange({ ...sort, sort_direction: e.target.value })}
                  label="Sort Direction"
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default LeadFilters;
